import { useState, useEffect } from 'react';
import { getAllFunds, getAllApprovalSequences, saveApprovalSequence } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import ToggleField from '../../shared/Forms/ToggleField';
import Button from '../../shared/UI/Button';

const ROLE_OPTIONS = [
  { value: 'head_of_pipeline', label: 'Head of Pipeline' },
  { value: 'cio', label: 'CIO' },
  { value: 'md', label: 'MD' },
];

const ROLE_NAMES = {
  head_of_pipeline: 'Head of Pipeline Review',
  cio: 'CIO Review',
  md: 'MD Review',
};

const DEFAULT_STAGES = [
  { stage_order: 1, role: 'head_of_pipeline', stage_name: 'Head of Pipeline Review', can_send_back: true },
  { stage_order: 2, role: 'cio', stage_name: 'CIO Review', can_send_back: false },
  { stage_order: 3, role: 'md', stage_name: 'MD Review', can_send_back: false },
];

export default function ApprovalSequenceForm({ sequence, onSaved, onCancel }) {
  const { user } = useAuth();
  const isNew = !sequence?.id;

  const [form, setForm] = useState(sequence
    ? { ...sequence }
    : { fund_id: '', name: '', stages: DEFAULT_STAGES.map(s => ({ ...s })) }
  );
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [funds, setFunds] = useState([]);
  const [existingSequences, setExistingSequences] = useState([]);
  const [duplicateError, setDuplicateError] = useState('');

  useEffect(() => {
    Promise.all([
      getAllFunds().then(list => list.filter(f => f.is_active !== false)),
      getAllApprovalSequences(),
    ]).then(([f, s]) => {
      setFunds(f);
      setExistingSequences(s);
    });
  }, []);

  // Check for duplicate when fund_id changes (only for new sequences)
  useEffect(() => {
    if (!isNew || !form.fund_id) {
      setDuplicateError('');
      return;
    }
    const exists = existingSequences.some(s => s.fund_id === form.fund_id);
    if (exists) {
      setDuplicateError(
        'An approval sequence already exists for this fund. Edit the existing sequence instead.'
      );
    } else {
      setDuplicateError('');
    }
  }, [form.fund_id, existingSequences, isNew]);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function setStage(idx, field, value) {
    const updated = form.stages.map((s, i) => {
      if (i !== idx) return s;
      const next = { ...s, [field]: value };
      if (field === 'role') next.stage_name = ROLE_NAMES[value] || value;
      return next;
    });
    if (field === 'can_send_back' && value === true) {
      updated.forEach((s, i) => { if (i !== idx) s.can_send_back = false; });
    }
    setForm(prev => ({ ...prev, stages: updated }));
  }

  function addStage() {
    if (form.stages.length >= 5) return;
    setForm(prev => ({
      ...prev,
      stages: [...prev.stages, {
        stage_order: prev.stages.length + 1,
        role: 'head_of_pipeline',
        stage_name: 'Head of Pipeline Review',
        can_send_back: false,
      }],
    }));
  }

  function removeStage(idx) {
    if (form.stages.length <= 1) return;
    setForm(prev => ({
      ...prev,
      stages: prev.stages.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stage_order: i + 1 })),
    }));
  }

  function moveUp(idx) {
    if (idx === 0) return;
    const arr = [...form.stages];
    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    setForm(prev => ({ ...prev, stages: arr.map((s, i) => ({ ...s, stage_order: i + 1 })) }));
  }

  function moveDown(idx) {
    if (idx === form.stages.length - 1) return;
    const arr = [...form.stages];
    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    setForm(prev => ({ ...prev, stages: arr.map((s, i) => ({ ...s, stage_order: i + 1 })) }));
  }

  function validate() {
    const e = {};
    if (!form.fund_id) e.fund_id = 'Fund is required';
    if (!form.name?.trim()) e.name = 'Sequence name is required';
    if (!form.stages?.length) e._stages = 'At least one stage is required';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isNew && duplicateError) return; // Block if duplicate
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await saveApprovalSequence({
        seqId: sequence?.id || null,
        seqData: { ...form },
        performedBy: user.uid,
      });
      setSuccessMsg('Approval sequence saved successfully!');
      setTimeout(() => { setSuccessMsg(''); onSaved(); }, 2000);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  const fundOptions = funds.map(f => ({ value: f.id, label: f.name }));

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {duplicateError && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>{duplicateError}</div>
      )}

      <div className="card mb-4">
        <div className="card-header"><h2>Basic Details</h2></div>
        <div className="card-body">
          <div className="form-grid">
            <SelectField label="Fund" required error={errors.fund_id}
              value={form.fund_id} onChange={e => set('fund_id', e.target.value)}
              options={fundOptions} />
            <FormField label="Sequence Name" required error={errors.name}>
              <input className={`form-input ${errors.name ? 'error' : ''}`}
                value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g. MIA Fund I Standard Approval" />
            </FormField>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h2>Approval Stages</h2>
          <Button type="button" size="sm" variant="secondary" onClick={addStage}
            disabled={form.stages.length >= 5}>+ Add Stage</Button>
        </div>
        <div className="card-body">
          {errors._stages && <div className="alert alert-error">{errors._stages}</div>}
          {form.stages.map((stage, idx) => (
            <div key={idx} style={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              padding: '12px 14px',
              marginBottom: 10,
              background: 'var(--color-card)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-text-secondary)', minWidth: 70 }}>
                  Stage {stage.stage_order}
                </span>
                <Button type="button" size="sm" variant="ghost" onClick={() => moveUp(idx)} disabled={idx === 0}>↑</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => moveDown(idx)} disabled={idx === form.stages.length - 1}>↓</Button>
                {form.stages.length > 1 && (
                  <Button type="button" size="sm" variant="danger" onClick={() => removeStage(idx)}>Remove</Button>
                )}
              </div>
              <div className="form-grid">
                <SelectField label="Role" value={stage.role}
                  onChange={e => setStage(idx, 'role', e.target.value)}
                  options={ROLE_OPTIONS} />
                <FormField label="Stage Display Name">
                  <input className="form-input" value={stage.stage_name}
                    onChange={e => setStage(idx, 'stage_name', e.target.value)} />
                </FormField>
                <ToggleField label="Can Send Back" checked={stage.can_send_back}
                  onChange={v => setStage(idx, 'can_send_back', v)}
                  hint="Only one stage can have send-back capability" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}
          disabled={isNew && !!duplicateError}>
          Save Sequence
        </Button>
      </div>
    </form>
  );
}
